import React, { useEffect, useState } from 'react';
import HealthStats from '../components/HealthStats';
import { usersAPI } from '../api/client';

export default function Profile({ user, setUser }) {
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name,
        height: user.height,
        weight: user.weight,
        age: user.age,
        gender: user.gender,
        activityLevel: user.activityLevel,
        healthGoal: user.healthGoal,
      });
    }
  }, [user]);

  if (!form) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'rgba(241,245,249,0.3)', fontFamily: 'Inter, sans-serif' }}>
      Loading profile…
    </div>
  );

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaved(false);
    setLoading(true);
    try {
      const res = await usersAPI.updateProfile({
        ...form,
        height: Number(form.height),
        weight: Number(form.weight),
        age: Number(form.age),
      });
      setUser(res.data.user);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const GOAL_LABELS = {
    weight_loss: 'Lose weight',
    maintain: 'Maintain weight',
    weight_gain: 'Gain weight',
    muscle_gain: 'Build muscle',
  };

  const ACTIVITY_LABELS = {
    sedentary: 'Sedentary',
    light: 'Lightly active',
    moderate: 'Moderately active',
    active: 'Active',
    very_active: 'Very active',
  };

  return (
    <div style={{ padding: '32px 28px 80px', maxWidth: 820, margin: '0 auto' }}>

      {/* Profile Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 24,
        marginBottom: 36,
        animation: 'fadeInUp 0.4s ease both',
      }}>
        {/* Avatar */}
        <div style={{
          width: 80, height: 80, borderRadius: '50%',
          background: 'linear-gradient(135deg, #a3e635 0%, #4ade80 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, fontWeight: 800, color: '#0a0d12',
          fontFamily: 'Inter, sans-serif', flexShrink: 0,
          boxShadow: '0 0 0 4px rgba(163,230,53,0.15), 0 0 0 8px rgba(163,230,53,0.06)',
        }}>
          {initials}
        </div>
        <div>
          <h1 style={{ fontSize: '1.7rem', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 4 }}>
            {user?.name}
          </h1>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Tag label={GOAL_LABELS[user?.healthGoal] || user?.healthGoal} color="#a3e635" />
            <Tag label={ACTIVITY_LABELS[user?.activityLevel] || user?.activityLevel} color="#818cf8" />
            <Tag label={`${user?.age} yrs`} color="#4ade80" />
          </div>
        </div>
      </div>

      {/* Health Stats */}
      <div style={{ marginBottom: 24, animation: 'fadeInUp 0.4s ease 0.1s both' }}>
        <HealthStats user={user} />
      </div>

      {/* Edit Form */}
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 20,
        padding: '28px 28px',
        animation: 'fadeInUp 0.4s ease 0.2s both',
      }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 24 }}>
          Edit profile
        </h3>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-banner">⚠️ {error}</div>}
          {saved && (
            <div className="success-banner">
              ✓ Profile updated — your BMI and calorie goal have been recalculated.
            </div>
          )}

          <div className="input-group">
            <label>Full name</label>
            <input type="text" name="name" value={form.name} onChange={handleChange} />
          </div>

          <div className="row">
            <div className="input-group">
              <label>Height (cm)</label>
              <input type="number" name="height" value={form.height} onChange={handleChange} />
            </div>
            <div className="input-group">
              <label>Weight (kg)</label>
              <input type="number" name="weight" value={form.weight} onChange={handleChange} />
            </div>
          </div>

          <div className="row">
            <div className="input-group">
              <label>Age</label>
              <input type="number" name="age" value={form.age} onChange={handleChange} />
            </div>
            <div className="input-group">
              <label>Gender</label>
              <select name="gender" value={form.gender} onChange={handleChange}>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="row">
            <div className="input-group">
              <label>Activity level</label>
              <select name="activityLevel" value={form.activityLevel} onChange={handleChange}>
                <option value="sedentary">Sedentary</option>
                <option value="light">Light</option>
                <option value="moderate">Moderate</option>
                <option value="active">Active</option>
                <option value="very_active">Very active</option>
              </select>
            </div>
            <div className="input-group">
              <label>Health goal</label>
              <select name="healthGoal" value={form.healthGoal} onChange={handleChange}>
                <option value="weight_loss">Lose weight</option>
                <option value="maintain">Maintain</option>
                <option value="weight_gain">Gain weight</option>
                <option value="muscle_gain">Build muscle</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ marginTop: 8, height: 46, paddingLeft: 28, paddingRight: 28 }}
            disabled={loading}
          >
            {loading ? <><span className="spinner" /> Saving…</> : 'Save changes →'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Tag({ label, color }) {
  return (
    <span style={{
      fontSize: 12, fontWeight: 600,
      color,
      background: `${color}18`,
      border: `1px solid ${color}30`,
      padding: '3px 10px', borderRadius: 99,
      fontFamily: 'Inter, sans-serif',
      letterSpacing: '0.01em',
    }}>
      {label}
    </span>
  );
}
